import { ProcessingLevelEnum } from "../../types";
import type { EventFilterValue, GPEventResponse } from "../../types";
import { GameProviderTagEnum } from "../definition";

export interface PlayerStatCalculationResult {
	url: string | null,
	playerId: number | null,
	statType: PlayerStatType,
	label: string,
	value: number | string | null,
	valueFormatted: string | null,
	valueSort: string | number | null,
	events: GPEventResponse[],
	filterValues: EventFilterValue[],
	isFilterable: boolean,
	isUseful: boolean,
}

export enum PlayerStatCategory {
	Summary = "summary",
	Offensive = "offensive",
	Defensive = "defensive",
	Distribution = "distribution",
	Goalkeeper = "goalkeeper",
	AdvancedMetrics = "advancedMetrics",
}

export enum PlayerStatType {
	Number = "number",
	Name = "name",
	Position = "position",
	Rating = "rating",
	MinutesPlayed = "minutesPlayed",
	AerialClearanceSuccess = "aerialClearanceSuccess",
	AerialClearance = "aerialClearance",
	AerialDuel = "aerialDuel",
	AerialDuelSuccess = "aerialDuelSuccess",
	AerialDuelRatio = "aerialDuelRatio",
	Assist = "assist",
	Block = "block",
	BlockAndParry = "blockAndParry",
	Card = "card",
	CardYellow = "cardYellow",
	CardRed = "cardRed",
	Catch = "catch",
	CatchInterception = "catchInterception",
	CatchInterceptionSuccess = "catchInterceptionSuccess",
	Corner = "corner",
	Cross = "cross",
	CrossSuccess = "crossSuccess",
	CrossRatio = "crossRatio",
	Foul = "foul",
	FoulWon = "foulWon",
	FoulCommited = "foulCommited",
	FreeKick = "freeKick",
	GoalKick = "goalKick",
	GoalKickSuccess = "goalKickSuccess",
	Goal = "goal",
	GoalOwn = "goalOwn",
	GoalConceded = "goalConceded",
	GroundDuel = "groundDuel",
	GroundDuelSuccess = "groundDuelSuccess",
	GroundDuelRatio = "groundDuelRatio",
	Interception = "interception",
	KeyPass = "keyPass",
	LoseBallRecovery = "loseBallRecovery",
	Mistake = "mistake",
	Offside = "offside",
	Pass = "pass",
	PassSuccess = "passSuccess",
	PassRatio = "passRatio",
	PassAccuracy = "passAccuracy",
	PassInFinalThird = "passInFinalThird",
	PassInFinalThirdSuccess = "passInFinalThirdSuccess",
	PassInFinalThirdRatio = "passInFinalThirdRatio",
	PassInMiddleThird = "passInMiddleThird",
	PassInMiddleThirdSuccess = "passInMiddleThirdSuccess",
	PassInMiddleThirdRatio = "passInMiddleThirdRatio",
	PassInDefensiveThird = "passInDefensiveThird",
	PassInDefensiveThirdSuccess = "passInDefensiveThirdSuccess",
	PassInDefensiveThirdRatio = "passInDefensiveThirdRatio",
	PassLong = "passLong",
	PassLongSuccess = "passLongSuccess",
	PassLongRatio = "passLongRatio",
	PassMedium = "passMedium",
	PassMediumSuccess = "passMediumSuccess",
	PassMediumRatio = "passMediumRatio",
	PassShort = "passShort",
	PassShortSuccess = "passShortSuccess",
	PassShortRatio = "passShortRatio",
	PassForward = "passForward",
	PassForwardSuccess = "passForwardSuccess",
	PassForwardRatio = "passForwardRatio",
	PassSideways = "passSideways",
	PassSidewaysSuccess = "passSidewaysSuccess",
	PassSidewaysRatio = "passSidewaysRatio",
	PassBackward = "passBackward",
	PassBackwardSuccess = "passBackwardSuccess",
	PassBackwardRatio = "passBackwardRatio",
	PassReceived = "passReceived",
	PassReceivedSuccess = "passReceivedSuccess",
	Punch = "punch",
	Recovery = "recovery",
	Save = "save",
	Shot = "shot",
	ShotOnTarget = "shotOnTarget",
	ShotBlocked = "shotBlocked",
	ShotOffTarget = "shotOffTarget",
	ShotInsidePa = "shotInsidePa",
	ShotOutsidePa = "shotOutsidePa",
	Tackle = "tackle",
	TackleSuccess = "tackleSuccess",
	TackleRatio = "tackleRatio",
	TakeOn = "takeOn",
	TakeOnAgainst = "takeOnAgainst",
	TakeOnAgainstSuccess = "takeOnAgainstSuccess",
	TakeOnSuccess = "takeOnSuccess",
	TakeOnRatio = "takeOnRatio",
	TechnicalError = "technicalError",
	ThrowIn = "throwIn",
	ShotConversion = "shotConversion",
	ThrowsRatio = "throwsRatio",
	AveragePassLength = "averagePassLength",
	LaunchPercent = "launchPercent",
	DefensiveActionsOutsidePenaltyArea = "defensiveActionsOutsidePenaltyArea",
	AverageDefensiveActionDistance = "averageDefensiveActionDistance",
	CrossesFaced = "crossesFaced",
	CrossesStoppedRatio = "crossesStoppedRatio",
	SavePercent = "savePercent",
	TotalXg = "totalXg",
	TotalXa = "totalXa",
	NonPenaltyXg = "nonPenaltyXg",
	XgPerShot = "xgPerShot",
	NonPenaltyGoal = "nonPenaltyGoal",
	ShotCreatingAction = "shotCreatingAction",
	PassIntoPenaltyArea = "passIntoPenaltyArea",
	SwitchOfPlay = "switchOfPlay",
	ProgressivePass = "progressivePass",
	DeepCompletion = "deepCompletion",
}

export const precomputedGoalkeeperStatTypes = new Set<PlayerStatType>([
	PlayerStatType.ThrowsRatio,
	PlayerStatType.AveragePassLength,
	PlayerStatType.LaunchPercent,
	PlayerStatType.DefensiveActionsOutsidePenaltyArea,
	PlayerStatType.AverageDefensiveActionDistance,
	PlayerStatType.CrossesFaced,
	PlayerStatType.CrossesStoppedRatio,
	PlayerStatType.SavePercent,
]);

export const expectedGoalsStatTypes = new Set<PlayerStatType>([
	PlayerStatType.TotalXg,
	PlayerStatType.TotalXa,
	PlayerStatType.NonPenaltyXg,
	PlayerStatType.XgPerShot,
]);

const playerStatDefinition = {
	[PlayerStatCategory.Summary]: [
		PlayerStatType.Number,
		PlayerStatType.Name,
		PlayerStatType.Position,
		PlayerStatType.Rating,
		PlayerStatType.MinutesPlayed,
		PlayerStatType.Goal,
		PlayerStatType.TotalXg,
		PlayerStatType.GoalOwn,
		PlayerStatType.Assist,
		PlayerStatType.TotalXa,
		PlayerStatType.Offside,
		PlayerStatType.Shot,
		PlayerStatType.FreeKick,
		PlayerStatType.Corner,
		PlayerStatType.ThrowIn,
		PlayerStatType.Foul,
		PlayerStatType.CardYellow,
		PlayerStatType.CardRed,
	],
	[PlayerStatCategory.Offensive]: [
		PlayerStatType.Number,
		PlayerStatType.Name,
		PlayerStatType.Position,
		PlayerStatType.ShotOnTarget,
		PlayerStatType.ShotBlocked,
		PlayerStatType.ShotOffTarget,
		PlayerStatType.ShotInsidePa,
		PlayerStatType.ShotOutsidePa,
		PlayerStatType.TakeOnRatio,
		PlayerStatType.ShotConversion,
		PlayerStatType.NonPenaltyGoal,
		PlayerStatType.ShotCreatingAction,

	],
	[PlayerStatCategory.Defensive]: [
		PlayerStatType.Number,
		PlayerStatType.Name,
		PlayerStatType.Position,
		PlayerStatType.TackleRatio,
		PlayerStatType.AerialDuelRatio,
		PlayerStatType.GroundDuelRatio,
		PlayerStatType.Interception,
		PlayerStatType.Recovery,
		PlayerStatType.Block,
		PlayerStatType.Mistake,
		PlayerStatType.FoulWon,
	],
	[PlayerStatCategory.Distribution]: [
		PlayerStatType.Number,
		PlayerStatType.Name,
		PlayerStatType.Position,
		PlayerStatType.PassRatio,
		PlayerStatType.PassAccuracy,
		PlayerStatType.KeyPass,
		PlayerStatType.PassInFinalThirdRatio,
		PlayerStatType.PassInMiddleThirdRatio,
		PlayerStatType.PassInDefensiveThirdRatio,
		PlayerStatType.PassLongRatio,
		PlayerStatType.PassMediumRatio,
		PlayerStatType.PassShortRatio,
		PlayerStatType.PassForwardRatio,
		PlayerStatType.PassSidewaysRatio,
		PlayerStatType.PassBackwardRatio,
		PlayerStatType.CrossRatio,
		PlayerStatType.PassIntoPenaltyArea,
		PlayerStatType.SwitchOfPlay,
		PlayerStatType.ProgressivePass,
		PlayerStatType.DeepCompletion,
	],
	[PlayerStatCategory.Goalkeeper]: [
		PlayerStatType.Number,
		PlayerStatType.Name,
		PlayerStatType.Position,
		PlayerStatType.GoalConceded,
		PlayerStatType.Catch,
		PlayerStatType.BlockAndParry,
		PlayerStatType.GoalKickSuccess,
		PlayerStatType.GoalKick,
		PlayerStatType.AerialClearanceSuccess,
		PlayerStatType.AerialClearance,
		PlayerStatType.ThrowsRatio,
		PlayerStatType.AveragePassLength,
		PlayerStatType.LaunchPercent,
		PlayerStatType.DefensiveActionsOutsidePenaltyArea,
		PlayerStatType.AverageDefensiveActionDistance,
		PlayerStatType.CrossesFaced,
		PlayerStatType.CrossesStoppedRatio,
		PlayerStatType.SavePercent,
	],
	[PlayerStatCategory.AdvancedMetrics]: [
		PlayerStatType.Number,
		PlayerStatType.Name,
		PlayerStatType.Position,
		PlayerStatType.TotalXg,
		PlayerStatType.TotalXa,
		PlayerStatType.NonPenaltyXg,
		PlayerStatType.XgPerShot,
	],
}

export const hintedColumns = [
	PlayerStatType.AerialClearance,
	PlayerStatType.AerialClearanceSuccess,
	PlayerStatType.AerialDuelRatio,
	PlayerStatType.Assist,
	PlayerStatType.Block,
	PlayerStatType.CardRed,
	PlayerStatType.CardYellow,
	PlayerStatType.Catch,
	PlayerStatType.Corner,
	PlayerStatType.CrossRatio,
	PlayerStatType.Foul,
	PlayerStatType.FoulWon,
	PlayerStatType.FreeKick,
	PlayerStatType.Goal,
	PlayerStatType.GoalConceded,
	PlayerStatType.GoalKick,
	PlayerStatType.GoalKickSuccess,
	PlayerStatType.GroundDuelRatio,
	PlayerStatType.Interception,
	PlayerStatType.KeyPass,
	PlayerStatType.MinutesPlayed,
	PlayerStatType.Mistake,
	PlayerStatType.Offside,
	PlayerStatType.PassAccuracy,
	PlayerStatType.PassBackwardRatio,
	PlayerStatType.PassForwardRatio,
	PlayerStatType.PassInDefensiveThirdRatio,
	PlayerStatType.PassInFinalThirdRatio,
	PlayerStatType.PassInMiddleThirdRatio,
	PlayerStatType.PassLongRatio,
	PlayerStatType.PassMediumRatio,
	PlayerStatType.PassShortRatio,
	PlayerStatType.PassSidewaysRatio,
	PlayerStatType.Punch,
	PlayerStatType.Rating,
	PlayerStatType.Recovery,
	PlayerStatType.Shot,
	PlayerStatType.ShotOnTarget,
	PlayerStatType.ShotOffTarget,
	PlayerStatType.ShotBlocked,
	PlayerStatType.ShotInsidePa,
	PlayerStatType.ShotOutsidePa,
	PlayerStatType.TackleRatio,
	PlayerStatType.TakeOnRatio,
	PlayerStatType.ThrowIn,
	PlayerStatType.ShotConversion,
	PlayerStatType.ThrowsRatio,
	PlayerStatType.AveragePassLength,
	PlayerStatType.LaunchPercent,
	PlayerStatType.DefensiveActionsOutsidePenaltyArea,
	PlayerStatType.AverageDefensiveActionDistance,
	PlayerStatType.CrossesFaced,
	PlayerStatType.CrossesStoppedRatio,
	PlayerStatType.SavePercent,
	PlayerStatType.NonPenaltyGoal,
	PlayerStatType.ShotCreatingAction,
	PlayerStatType.PassIntoPenaltyArea,
	PlayerStatType.SwitchOfPlay,
	PlayerStatType.ProgressivePass,
	PlayerStatType.DeepCompletion,
];

const playerStatDefinitionLight: Record<PlayerStatCategory, PlayerStatType[]> = {
	[PlayerStatCategory.Summary]: playerStatDefinition[PlayerStatCategory.Summary].filter(
		(statType) => expectedGoalsStatTypes.has(statType) === false,
	),
	[PlayerStatCategory.Offensive]: [
		PlayerStatType.Number,
		PlayerStatType.Name,
		PlayerStatType.Position,
		PlayerStatType.ShotOnTarget,
		PlayerStatType.ShotBlocked,
		PlayerStatType.ShotOffTarget,
		PlayerStatType.TakeOnRatio,
		PlayerStatType.ShotConversion,

	],
	[PlayerStatCategory.Defensive]: [
		PlayerStatType.Number,
		PlayerStatType.Name,
		PlayerStatType.Position,
		PlayerStatType.TackleRatio,
		PlayerStatType.AerialDuelRatio,
		PlayerStatType.GroundDuelRatio,
		PlayerStatType.Interception,
		PlayerStatType.Block,
		PlayerStatType.Mistake,
		PlayerStatType.FoulWon,
	],
	[PlayerStatCategory.Distribution]: [
		PlayerStatType.Number,
		PlayerStatType.Name,
		PlayerStatType.Position,
		PlayerStatType.KeyPass,
	],
	[PlayerStatCategory.Goalkeeper]: playerStatDefinition[PlayerStatCategory.Goalkeeper].filter(
		(statType) => !precomputedGoalkeeperStatTypes.has(statType),
	),
	[PlayerStatCategory.AdvancedMetrics]: [],
}

export const getPlayerStatCategories = (processingLevel?: ProcessingLevelEnum): PlayerStatCategory[] => {
	const isLimited = processingLevel === ProcessingLevelEnum.Limited;

	return Object.values(PlayerStatCategory).filter(
		(category) => isLimited === false || category !== PlayerStatCategory.AdvancedMetrics,
	);
}

export const getPlayerStatDefinitionByCategory = (
	category: PlayerStatCategory,
	showRatings: boolean,
	processingLevel?: ProcessingLevelEnum,
	providerTag?: string | null,
	teamTagged?: string | null,
): PlayerStatType[] => {
	const isLimited = processingLevel === ProcessingLevelEnum.Limited;
	const hideEnhancedGoalkeeperStats =
		isLimited || providerTag?.toUpperCase() === GameProviderTagEnum.UBITRACK;
	const omitOneTeamTaggingStats = teamTagged !== undefined && teamTagged !== null && teamTagged !== "both";

	const definition = isLimited
		? (playerStatDefinitionLight[category] ?? playerStatDefinition[category] ?? [])
		: (playerStatDefinition[category] ?? []);

	return definition.filter((statType) => {
		if (showRatings === false && statType === PlayerStatType.Rating) {
			return false;
		}

		if (hideEnhancedGoalkeeperStats && precomputedGoalkeeperStatTypes.has(statType)) {
			return false;
		}

		if (
			omitOneTeamTaggingStats
			&& (statType === PlayerStatType.Recovery
				|| statType === PlayerStatType.FoulWon
				|| statType === PlayerStatType.TakeOn
				|| statType === PlayerStatType.TakeOnSuccess
				|| statType === PlayerStatType.TakeOnRatio)
		) {
			return false;
		}

		return true;
	});
}
